import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useUser } from '../contexts/UserContext';
import { PaperAirplaneIcon, ChartBarIcon, LinkIcon, DocumentTextIcon, ArrowPathIcon, Cog6ToothIcon, BeakerIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { chatbotApi, modelsApi, discordApi, converseApi, statisticsApi } from '../services/api';
import { Chatbot, KnowledgeBaseDocument, Model } from '../types';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';
import ReactMarkdown from 'react-markdown';
import { config } from '../services/config';
import DiscordServerModal from '../components/DiscordServerModal';
import FileReplaceConfirmDialog from '../components/FileReplaceConfirmDialog';
import SessionsModal from '../components/SessionsModal';
import AllowedDomainsModal from '../components/AllowedDomainsModal';
import KnowledgePanel, { ACCEPTED_EXTENSIONS, MAX_FILE_BYTES } from '../components/knowledge/KnowledgePanel';
import ModelField from '../components/ModelField';
import TabHelp from '../components/TabHelp';

// The per-message and per-conversation credit figures now arrive from `/models`,
// computed server-side. They used to be derived here from a hardcoded TYPICAL_TURN
// profile plus a copy of CREDIT_PER_KB_SEARCH — two billing constants living in
// TypeScript, where nothing connected them to what converse actually charges. The
// profile also modelled no prompt caching at all, so it drifted further out as caching
// started paying. See REFERENCE_TURN in stacks/api/src/models/handler.py.

const chatbotTabs = [
  { name: 'Settings', href: 'settings', icon: Cog6ToothIcon },
  { name: 'Knowledge', href: 'knowledge', icon: DocumentTextIcon },
  { name: 'Test', href: 'test', icon: BeakerIcon },
  { name: 'Integration', href: 'integration', icon: LinkIcon },
  { name: 'Stats', href: 'stats', icon: ChartBarIcon },
];

export default function ChatbotForm() {
  const navigate = useNavigate();
  const { chatbotId, section } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { refreshProfile, usage } = useUser();
  const isEditing = !!chatbotId;
  
  // Get active section from URL, default to settings
  const activeSection = section || 'settings';

  // Use ref to store polling interval to ensure proper cleanup
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(true);
  // Typed as Model rather than an inline shape: the picker now reads isDefault and the
  // server-derived credit figures, and an inline duplicate of the type would silently
  // drop whichever fields it forgot.
  const [models, setModels] = useState<Model[]>([]);
  const [messages, setMessages] = useState<Array<{id: string, text: string, isUser: boolean, timestamp: Date}>>([
    { id: '1', text: 'Hello! How can I help you today?', isUser: false, timestamp: new Date() }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    status: string;
    jobId?: string;
    startTime?: string;
    endTime?: string;
    documentsCount: number;
    pendingDocuments: number;
    errorMessage?: string;
    canUpload: boolean;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Refs for auto-scrolling messages and input focus
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    modelId: '',  // Will be set from models API
    customPrompt: '',
    assistantIdentity: '',
    botUnavailMsg: '',
    // New chatbots default to documents-only. The create call sends this in
    // formData, so the bot is persisted 'strict'. (Existing bots without the
    // field still resolve to 'augmented' on load below, matching how converse
    // treats a missing knowledge_mode — the backend default is unchanged.)
    knowledgeMode: 'strict' as 'strict' | 'augmented',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [, setChatbot] = useState<Chatbot | null>(null);
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [fileConflict, setFileConflict] = useState<{
    file: File;
    existingDocument: any;
  } | null>(null);
  const [pendingConflicts, setPendingConflicts] = useState<Array<{ file: File; existingDocument: any }>>([]);
  const [pendingDeletion, setPendingDeletion] = useState<Set<string>>(new Set());
  // Keyed by the temporary document id, 0-100, live from the XHR upload.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [discordIntegrations, setDiscordIntegrations] = useState<Array<{
    server_id: string;
    server_name: string;
    chatbot_id: string;
    status: string;
    created_at: string;
    updated_at: string;
  }>>([]);
  const [discordLinkStatus, setDiscordLinkStatus] = useState<{
    linked: boolean;
    discord_username: string | null;
    method: 'federated' | 'linked' | 'none';
    token_valid: boolean;
  } | null>(null);

  // Statistics state
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsTimeRange, setStatisticsTimeRange] = useState(7);
  const [dashboardData, setDashboardData] = useState<{
    messages_over_time: Array<{date: string, count: number}>;
    active_sessions: Array<{date: string, count: number}>;
    top_chatbots: Array<{chatbot_id: string, name: string, message_count: number}>;
    response_times: Array<{date: string, avg_response_time_ms: number}>;
    session_depths: Array<{bucket: string, count: number}>;
    platform_usage: Array<{platform: string, count: number}>;
  } | null>(null);
  const [sessionsData, setSessionsData] = useState<Array<{
    session_id: string;
    chatbot_id: string;
    platform: string;
    created_at: number;
    last_interaction: number;
    total_messages: number;
    duration_seconds: number;
    status: string;
  }>>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [conversationData, setConversationData] = useState<Record<string, Array<{
    message_type: string;
    content: string;
    timestamp: string;
    message_sequence: number;
    message_length: number;
    model_id?: string;
    response_time_ms?: number;
  }>>>({});
  const [showSessionsModal, setShowSessionsModal] = useState(false);

  useEffect(() => {
    loadModels();
    if (isEditing && chatbotId) {
      loadChatbot(chatbotId);
    }
  }, [isEditing, chatbotId]);

  // Load integrations and link status when integration tab is active
  useEffect(() => {
    if (isEditing && chatbotId && activeSection === 'integration' && isAuthenticated && !authLoading) {
      loadDiscordIntegrations();
      loadDiscordLinkStatus();
    }
  }, [activeSection, isEditing, chatbotId, isAuthenticated, authLoading]);

  // Listen for Discord link popup messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'discord-link-code') {
        // Popup sent us the authorization code - exchange it via API
        const { code, redirectUri } = event.data;
        const popup = event.source as Window;

        try {
          toast.loading('Linking Discord account...', { id: 'discord-link' });
          const response = await discordApi.linkDiscordAccount(code, redirectUri);

          if (response.success && response.data) {
            toast.dismiss('discord-link');
            toast.success(`Discord account linked: ${response.data.discord_username}`);
            loadDiscordLinkStatus();
            popup?.postMessage({ type: 'discord-link-close', success: true }, window.location.origin);
          } else {
            throw new Error((response as any).error || 'Unknown error');
          }
        } catch (err: any) {
          toast.dismiss('discord-link');
          const errorMsg = err.response?.data?.error || err.message || 'Failed to link Discord account';
          toast.error(errorMsg);
          popup?.postMessage({ type: 'discord-link-close', success: false, error: errorMsg }, window.location.origin);
        }
      } else if (event.data.type === 'discord-link-error') {
        toast.error(event.data.error || 'Failed to link Discord account');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load documents and sync status when editing
  useEffect(() => {
    if (isEditing && chatbotId) {
      loadDocuments();
      loadSyncStatus('useEffect-editing');
    }
  }, [isEditing, chatbotId]);

  // Refresh documents when switching to knowledge tab
  useEffect(() => {
    if (isEditing && chatbotId && activeSection === 'knowledge') {
      loadDocuments();
      loadSyncStatus('useEffect-knowledge-tab');
    }
  }, [activeSection, isEditing, chatbotId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Load statistics when tab is active or time range changes
  useEffect(() => {
    if (isEditing && chatbotId && activeSection === 'stats') {
      loadStatistics();
    }
  }, [activeSection, statisticsTimeRange, isEditing, chatbotId]);

  // Auto-focus message input when test tab becomes active
  useEffect(() => {
    if (activeSection === 'test') {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [activeSection]);

  const loadModels = async () => {
    try {
      setModelsLoading(true);
      const response = await modelsApi.getModels();
      if (response.success && response.data) {
        setModels(response.data);
        // Preselect the model flagged as default, falling back to the first (the list
        // arrives sorted cheapest-first). Only applies to a NEW chatbot — an existing
        // one has already hydrated formData.modelId, so changing the default never
        // moves a chatbot that is already running.
        if (!formData.modelId && response.data.length > 0) {
          const preferred = response.data.find(m => m.isDefault) ?? response.data[0];
          setFormData(prev => ({ ...prev, modelId: preferred.id }));
        }
      } else {
        console.error('Failed to load models:', response);
        toast.error('Failed to load available models');
      }
    } catch (error) {
      console.error('Error loading models:', error);
      toast.error('Failed to load available models');
    } finally {
      setModelsLoading(false);
    }
  };

  const loadChatbot = async (id: string) => {
    try {
      setLoading(true);
      const response = await chatbotApi.getChatbot(id);
      if (response.success && response.data) {
        const chatbotData = response.data;
        setChatbot(chatbotData);
        setFormData({
          name: chatbotData.name || '',
          description: chatbotData.description || '',
          modelId: chatbotData.modelId,
          customPrompt: chatbotData.customPrompt || '',
          assistantIdentity: chatbotData.assistantIdentity || '',
          botUnavailMsg: chatbotData.botUnavailMsg || '',
          knowledgeMode: chatbotData.knowledgeMode || 'augmented',
        });
        // Load allowed embed domains
        setAllowedEmbedDomains(chatbotData.allowedEmbedDomains || []);
      } else {
        toast.error('Failed to load chatbot');
        navigate('/chatbots');
      }
    } catch (error) {
      console.error('Error loading chatbot:', error);
      toast.error('Failed to load chatbot');
      navigate('/chatbots');
    } finally {
      setLoading(false);
    }
  };

  const loadDiscordIntegrations = async () => {
    try {
      const response = await discordApi.getDiscordIntegrations();
      if (response.success && response.data) {
        // Filter integrations for this specific chatbot
        const chatbotIntegrations = response.data.integrations.filter(
          integration => integration.chatbot_id === chatbotId
        );
        setDiscordIntegrations(chatbotIntegrations);
      }
    } catch (error) {
      console.error('Error loading Discord integrations:', error);
      // Don't show error toast for this as it's not critical
    }
  };

  const handleDiscordIntegrationSuccess = () => {
    // Reload Discord integrations after successful creation
    loadDiscordIntegrations();
  };

  const loadDiscordLinkStatus = async () => {
    try {
      const response = await discordApi.getDiscordLinkStatus();
      if (response.success && response.data) {
        setDiscordLinkStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading Discord link status:', error);
    }
  };

  const handleLinkDiscord = () => {
    const clientId = config.DISCORD_CLIENT_ID;
    if (!clientId) {
      toast.error('Discord integration not configured');
      return;
    }

    // Use static HTML callback page to avoid Amplify intercepting the ?code= parameter
    const redirectUri = `${window.location.origin}/auth/discord-link.html`;
    const state = crypto.randomUUID();
    sessionStorage.setItem('discord_link_state', state);

    const discordAuthUrl = `https://discord.com/oauth2/authorize` +
      `?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent('identify email guilds')}` +
      `&state=${state}`;

    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      discordAuthUrl,
      'discord-link',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const handleDeleteDiscordIntegration = async (serverId: string, serverName: string) => {
    try {
      const response = await discordApi.deleteDiscordIntegration(serverId);
      if (response.success) {
        toast.success(`Disconnected from Discord server "${serverName}"`);
        loadDiscordIntegrations();
      } else {
        toast.error('Failed to disconnect Discord integration');
      }
    } catch (error) {
      console.error('Error deleting Discord integration:', error);
      toast.error('Failed to disconnect Discord integration');
    }
  };

  const [showEmbedModal, setShowEmbedModal] = useState(false);

  // Domain whitelist for website embeds
  const [allowedEmbedDomains, setAllowedEmbedDomains] = useState<string[]>([]);
  const [showDomainsModal, setShowDomainsModal] = useState(false);

  const handleGetEmbedCode = () => {
    setShowEmbedModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Integration code copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Domain whitelist management
  const handleAddDomain = async (domainToAdd: string): Promise<boolean> => {
    if (!chatbotId) return false;

    const updatedDomains = [...allowedEmbedDomains, domainToAdd];

    try {
      const response = await chatbotApi.updateChatbot(chatbotId, {
        allowedEmbedDomains: updatedDomains
      });

      if (response.success) {
        setAllowedEmbedDomains(updatedDomains);
        toast.success(`Added ${domainToAdd} to allowed domains`);
        return true;
      } else {
        toast.error('Failed to add domain');
        return false;
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      toast.error('Failed to add domain');
      return false;
    }
  };

  const handleRemoveDomain = async (domainToRemove: string): Promise<boolean> => {
    if (!chatbotId) return false;

    const updatedDomains = allowedEmbedDomains.filter(d => d !== domainToRemove);

    try {
      const response = await chatbotApi.updateChatbot(chatbotId, {
        allowedEmbedDomains: updatedDomains
      });

      if (response.success) {
        setAllowedEmbedDomains(updatedDomains);
        toast.success(`Removed ${domainToRemove} from allowed domains`);
        return true;
      } else {
        toast.error('Failed to remove domain');
        return false;
      }
    } catch (error) {
      console.error('Error removing domain:', error);
      toast.error('Failed to remove domain');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a chatbot name');
      return;
    }

    setLoading(true);
    try {
      if (isEditing && chatbotId) {
        const response = await chatbotApi.updateChatbot(chatbotId, formData);
        if (response.success) {
          toast.success('Chatbot updated successfully');
          // Stay on the current tab instead of navigating away
        } else {
          toast.error('Failed to update chatbot');
        }
      } else {
        const response = await chatbotApi.createChatbot(formData);
        if (response.success) {
          toast.success('Chatbot created successfully');
          navigate('/dashboard');
        } else {
          toast.error('Failed to create chatbot');
        }
      }
    } catch (error: any) {
      console.error('Error saving chatbot:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save chatbot';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending || !chatbotId) return;

    const userMessage = {
      id: Date.now().toString(),
      text: newMessage,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsSending(true);

    try {
      // Call the converse API
      const response = await converseApi.sendMessage(chatbotId, userMessage.text, sessionId, 'web');
      
      if (response.success && response.data) {
        // Store the session ID for subsequent messages
        if (response.data.session_id) {
          setSessionId(response.data.session_id);
        }

        // Refresh credit balance in navbar
        refreshProfile();

        const botMessage = {
          id: (Date.now() + 1).toString(),
          text: response.data.response,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error('Failed to get response from chatbot');
      }
    } catch (error) {
      console.error('Error sending message:', error);

      // Extract specific error message from API response if available
      let errorText = 'Sorry, I encountered an error processing your message. Please try again.';
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorText = error.response.data.message;
      }

      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: errorText,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
      // Re-focus the input field for continuous typing
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length || !chatbotId) {
      return;
    }

    // Check if uploads are allowed
    if (syncStatus && !syncStatus.canUpload) {
      toast.error('Cannot upload files while your knowledge base is updating');
      return;
    }

    setIsUploading(true);
    const filesToUpload = files;
    const filesToProcess: File[] = [];

    try {
      // First, validate all files and check for conflicts
      for (const file of filesToUpload) {
        // Validate file size (10MB limit)
        if (file.size > MAX_FILE_BYTES) {
          toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
          continue;
        }

        // Validate file type - check both MIME type and file extension
        const allowedMimeTypes = [
          'application/pdf', 
          'application/msword', 
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
          'text/plain', 
          'text/markdown'
        ];
        
        const fileName = file.name.toLowerCase();
        const hasValidExtension = ACCEPTED_EXTENSIONS.some(ext => fileName.endsWith(ext));
        const hasValidMimeType = allowedMimeTypes.includes(file.type);
        
        // Accept file if it has valid extension OR valid MIME type (handles .md files with various MIME types)
        if (!hasValidExtension && !hasValidMimeType) {
          toast.error(`File "${file.name}" type not supported. Use PDF, DOC, DOCX, TXT, or MD files.`);
          continue;
        }

        // Note: We'll handle conflicts during the actual upload attempt

        filesToProcess.push(file);
      }


      // Upload files that don't have conflicts
      if (filesToProcess.length > 0) {
        await processFileUploads(filesToProcess, false);
      } else {
        // No valid files to upload
        toast.error('No valid files selected for upload');
      }
    } catch (error: any) {
      toast.error('Upload process failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Uploads run three at a time with per-file progress. Filename conflicts are
  // collected and asked about afterwards — the previous version bailed out of
  // the loop on the first 409, silently dropping every file behind it.
  const UPLOAD_CONCURRENCY = 3;

  const processFileUploads = async (files: File[], replaceExisting: boolean = false) => {
    if (!chatbotId) return;

    const conflicts: Array<{ file: File; existingDocument: any }> = [];
    let successfulUploads = 0;

    const uploadOne = async (file: File) => {
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const tempDoc: KnowledgeBaseDocument = {
        id: tempId,
        name: file.name,
        type: 'file',
        status: 'uploading',
        size: file.size,
        uploadedAt: new Date().toISOString(),
        contentType: file.type
      };

      setDocuments(prev => [tempDoc, ...prev]);
      setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

      try {
        await chatbotApi.uploadDocument(chatbotId, file, replaceExisting, percent => {
          setUploadProgress(prev => ({ ...prev, [tempId]: percent }));
        });
        successfulUploads++;
      } catch (error: any) {
        // Collect the conflict rather than aborting the whole batch.
        if (error.response?.status === 409 && !replaceExisting) {
          conflicts.push({
            file,
            existingDocument: error.response?.data?.data?.existingDocument || {
              name: file.name,
              status: 'unknown',
              id: 'unknown'
            }
          });
        } else {
          toast.error(`Failed to ${replaceExisting ? 'replace' : 'upload'} "${file.name}"`);
        }
      } finally {
        setDocuments(prev => prev.filter(doc => doc.id !== tempId));
        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      }
    };

    try {
      const queue = [...files];
      const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, async () => {
        let next = queue.shift();
        while (next) {
          await uploadOne(next);
          next = queue.shift();
        }
      });
      await Promise.all(workers);

      // Refresh once for the whole batch rather than once per file.
      await loadDocuments();

      if (successfulUploads > 0) {
        toast.success(
          successfulUploads === 1
            ? `1 file ${replaceExisting ? 'replaced' : 'uploaded'}`
            : `${successfulUploads} files ${replaceExisting ? 'replaced' : 'uploaded'}`
        );
      }

      // Ask about conflicts one at a time; the rest wait in pendingConflicts.
      if (conflicts.length > 0) {
        setFileConflict(conflicts[0]);
        setPendingConflicts(conflicts.slice(1));
      }
    } catch (error) {
      toast.error('Upload process failed');
    }
  };

  const handleFileReplaceConfirm = async () => {
    if (!fileConflict) return;

    const { file } = fileConflict;
    const remaining = pendingConflicts;
    setFileConflict(null);
    setPendingConflicts([]);
    setIsUploading(true);

    try {
      await processFileUploads([file], true);
    } finally {
      setIsUploading(false);
      if (remaining.length > 0) {
        setFileConflict(remaining[0]);
        setPendingConflicts(remaining.slice(1));
      }
    }
  };

  const handleFileReplaceCancel = () => {
    const remaining = pendingConflicts;
    setFileConflict(null);
    setPendingConflicts([]);
    if (remaining.length > 0) {
      setFileConflict(remaining[0]);
      setPendingConflicts(remaining.slice(1));
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    // Staged only — the delete is committed by Update knowledge.
    setPendingDeletion(prev => new Set([...Array.from(prev), documentId]));
  };

  // Reverts everything the commit bar is counting: staged removals go back, and
  // files uploaded but never indexed are deleted. The second half is a real
  // delete, so it is confirmed.
  const handleDiscardChanges = async () => {
    if (!chatbotId) return;

    const unindexed = documents.filter(doc => doc.status === 'uploaded');
    if (unindexed.length > 0) {
      const noun = unindexed.length === 1 ? 'file' : 'files';
      if (!window.confirm(`Discard ${unindexed.length} newly added ${noun}? They will be removed.`)) {
        return;
      }
    }

    setPendingDeletion(new Set());

    try {
      await Promise.all(
        unindexed.map(doc =>
          chatbotApi.deleteDocument(chatbotId, doc.id).catch(err => {
            console.error(`Failed to discard document ${doc.id}:`, err);
            return null;
          })
        )
      );
    } finally {
      await loadDocuments();
    }
  };

  // The Knowledge tab has no submit button of its own — the form's submit lives
  // on Settings — so this saves on change. handleSubmit never sent this field.
  const handleKnowledgeModeChange = async (mode: 'strict' | 'augmented') => {
    const previous = formData.knowledgeMode;
    if (mode === previous) return;

    setFormData(prev => ({ ...prev, knowledgeMode: mode }));

    if (!isEditing || !chatbotId) return;

    try {
      await chatbotApi.updateChatbot(chatbotId, { knowledgeMode: mode } as any);
      toast.success(mode === 'strict' ? 'Answering from your documents only' : 'Answering from your documents first');
    } catch {
      setFormData(prev => ({ ...prev, knowledgeMode: previous }));
      toast.error('Could not save that setting');
    }
  };

  const undoPendingDeletion = (docId: string) => {
    setPendingDeletion(prev => {
      const next = new Set(prev);
      next.delete(docId);
      return next;
    });
  };

  const loadDocuments = async () => {
    if (!chatbotId) return;

    try {
      console.log('Loading documents for chatbot:', chatbotId);
      const response = await chatbotApi.getKnowledgeBaseDocuments(chatbotId);
      if (response.data) {
        // The API returns { documents: KnowledgeBaseDocument[] }
        let newDocuments = response.data.documents || [];

        // Check which pending deletions are now confirmed (no longer in backend)
        const backendDocIds = new Set(newDocuments.map(doc => doc.id));
        const confirmedDeletions = Array.from(pendingDeletion).filter(id => !backendDocIds.has(id));

        // Clear confirmed deletions from pendingDeletion
        if (confirmedDeletions.length > 0) {
          setPendingDeletion(prev => {
            const next = new Set(prev);
            confirmedDeletions.forEach(id => next.delete(id));
            return next;
          });
        }

        // Filter out documents that are still pending deletion (not yet confirmed by backend)
        newDocuments = newDocuments.filter(doc => !pendingDeletion.has(doc.id));

        // Sort by upload date, most recent first
        newDocuments.sort((a, b) => {
          const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
          const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
          return dateB - dateA;
        });
        console.log('Loaded documents with statuses:', newDocuments.map(doc => ({ name: doc.name, status: doc.status, id: doc.id })));
        setDocuments(newDocuments);
      }
    } catch (error: any) {
      console.error('Error loading documents:', error);
      
      // If chatbot doesn't exist (404), stop polling and redirect
      if (error?.response?.status === 404) {
        console.log('Chatbot not found (404) while loading documents, stopping polling');
        stopPolling();
        toast.error('Chatbot no longer exists');
        navigate('/chatbots');
        return;
      }
      
      // Don't show error toast if user navigated away, to avoid interruptions
      if (activeSection === 'knowledge') {
        toast.error('Failed to load documents');
      }
    }
  };

  const loadSyncStatus = async (source: string = 'unknown') => {
    if (!chatbotId) return;

    console.log(`[DEBUG] loadSyncStatus called from "${source}": chatbotId=${chatbotId}, isPolling=${isPolling}, intervalExists=${!!pollingIntervalRef.current}`);

    try {
      const response = await chatbotApi.getSyncStatus(chatbotId);
      if (response.data) {
        const oldStatus = syncStatus?.status;
        const newStatus = response.data.status;
        console.log('Sync status update:', { 
          oldStatus, 
          newStatus, 
          jobId: response.data.jobId,
          documentsCount: response.data.documentsCount,
          pendingDocuments: response.data.pendingDocuments,
          canUpload: response.data.canUpload
        });
        
        // With EventBridge managing status updates, we can trust the backend status
        // No need for race condition protection since EventBridge ensures accurate updates
        setSyncStatus({
          ...response.data,
          documentsCount: response.data.documentsCount || 0,
          pendingDocuments: response.data.pendingDocuments || 0
        });
        
        // Start polling if sync is in progress
        console.log(`[DEBUG] Sync status logic: oldStatus="${oldStatus}", newStatus="${newStatus}", isPolling=${isPolling}, intervalExists=${!!pollingIntervalRef.current}`);
        
        if (newStatus === 'STARTING' || newStatus === 'IN_PROGRESS') {
          console.log('[DEBUG] Sync in progress, starting polling');
          startPolling();
        } else {
          console.log('[DEBUG] Sync not in progress, stopping polling');
          // Only stop polling if we're actually polling
          if (pollingIntervalRef.current) {
            stopPolling();
          } else {
            console.log('[DEBUG] No active polling to stop');
          }
          
          // If sync just completed, refresh documents to show updated status
          // Handle transitions from both STARTING and IN_PROGRESS (sync can be fast)
          if ((oldStatus === 'IN_PROGRESS' || oldStatus === 'STARTING') && newStatus === 'COMPLETE') {
            console.log('Sync completed, refreshing documents to update statuses');
            await loadDocuments();
            toast.success('Knowledge updated');
          } else if ((oldStatus === 'IN_PROGRESS' || oldStatus === 'STARTING') && newStatus === 'FAILED') {
            console.log('Sync failed, refreshing documents to update statuses');
            await loadDocuments();
            toast.error('Could not finish updating knowledge');
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading sync status:', error);
      
      // If chatbot doesn't exist (404), stop polling and redirect
      if (error?.response?.status === 404) {
        console.log('Chatbot not found (404) while loading sync status, stopping polling');
        stopPolling();
        toast.error('Chatbot no longer exists');
        navigate('/chatbots');
        return;
      }
    }
  };

  const startSyncJob = async () => {
    if (!chatbotId) return;

    // Instant feedback - update UI immediately before API call
    toast.success('Updating knowledge');
    setSyncStatus(prev => prev
      ? { ...prev, status: 'STARTING', canUpload: false }
      : { status: 'STARTING', canUpload: false, documentsCount: 0, pendingDocuments: 0 }
    );

    // Optimistically mark uploaded documents as processing and remove pending deletions from UI
    setDocuments(prev => prev
      .filter(doc => !pendingDeletion.has(doc.id))
      .map(doc => doc.status === 'uploaded' ? { ...doc, status: 'processing' } : doc)
    );

    try {
      // First, actually delete all pending deletion files
      if (pendingDeletion.size > 0) {
        console.log(`Deleting ${pendingDeletion.size} pending files...`);
        const deletePromises = Array.from(pendingDeletion).map(docId =>
          chatbotApi.deleteDocument(chatbotId, docId).catch(err => {
            console.error(`Failed to delete document ${docId}:`, err);
            return null; // Don't fail the whole batch
          })
        );
        await Promise.all(deletePromises);
        // Don't clear pendingDeletion here - loadDocuments will clear it
        // once the backend confirms the documents are gone
      }

      console.log('Starting sync job...');
      const response = await chatbotApi.startSync(chatbotId);
      console.log('Sync start response:', response);

      // Start polling to catch status changes from EventBridge
      startPolling();

      // Load initial status
      await loadSyncStatus('start-sync-job');

    } catch (error: any) {
      console.error('Error starting sync:', error);

      // Revert optimistic updates on error
      await loadDocuments();
      await loadSyncStatus('sync-job-error');

      if (error.response?.status === 409) {
        toast.error('An update is already running');
      } else if (error.response?.status === 400) {
        toast.error('Nothing new to read');
      } else {
        toast.error('Could not start the update');
      }
    }
  };

  const startPolling = () => {
    console.log(`[DEBUG] startPolling called: isPolling=${isPolling}, intervalExists=${!!pollingIntervalRef.current}`);
    
    if (isPolling || pollingIntervalRef.current) {
      console.log('[DEBUG] Polling already active, skipping startPolling');
      return;
    }
    
    console.log('[DEBUG] Starting new polling interval');
    setIsPolling(true);
    const interval = setInterval(async () => {
      console.log('[DEBUG] Polling interval tick - calling loadSyncStatus');
      try {
        await loadSyncStatus('polling-interval');
        // Only refresh documents if Knowledge tab is active for better performance
        if (activeSection === 'knowledge') {
          await loadDocuments();
        }
      } catch (error: any) {
        console.error('Polling error:', error);
        // Stop polling if chatbot no longer exists (404) or if there's a persistent error
        if (error?.response?.status === 404) {
          console.log('Chatbot not found (404), stopping polling and redirecting');
          stopPolling();
          toast.error('Chatbot no longer exists');
          navigate('/chatbots');
        }
      }
    }, 10000); // Poll every 10 seconds

    // Store interval reference in useRef for proper cleanup
    pollingIntervalRef.current = interval;
    console.log('[DEBUG] New polling interval created and stored');
  };

  const stopPolling = () => {
    console.log(`[DEBUG] stopPolling called: isPolling=${isPolling}, intervalExists=${!!pollingIntervalRef.current}`);
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      console.log('[DEBUG] Clearing polling interval');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('[DEBUG] Polling interval cleared successfully');
    } else {
      console.log('[DEBUG] No polling interval to clear');
    }
  };

  const loadStatistics = async () => {
    if (!chatbotId) return;

    try {
      setStatisticsLoading(true);
      
      // Reset expanded sessions and conversation data when time range changes
      setExpandedSessions(new Set());
      setConversationData({});
      
      // Load dashboard data and sessions in parallel
      const [dashboardResponse, sessionsResponse] = await Promise.all([
        chatbotApi.getChatbotStatistics(chatbotId, statisticsTimeRange),
        chatbotApi.getChatbotSessions(chatbotId, 20)
      ]);
      
      if (dashboardResponse.success) {
        // Set data even if empty - this is normal for unused chatbots
        setDashboardData(dashboardResponse.data || {
          messages_over_time: [],
          active_sessions: [],
          top_chatbots: [],
          response_times: [],
          session_depths: [],
          platform_usage: []
        });
      } else {
        toast.error('Failed to load statistics data');
        setDashboardData(null);
      }
      
      if (sessionsResponse.success) {
        // Set data even if empty array - this is normal for unused chatbots
        setSessionsData(sessionsResponse.data || []);
      } else {
        toast.error('Failed to load sessions data');
        setSessionsData([]);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Failed to load statistics');
      setDashboardData(null);
      setSessionsData([]);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const handleRefreshStatistics = async () => {
    await loadStatistics();
    toast.success('Statistics refreshed');
  };

  const handleStatisticsTimeRangeChange = (newTimeRange: number) => {
    setStatisticsTimeRange(newTimeRange);
  };

  const toggleSessionExpansion = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
      // Load conversation data if not already loaded
      if (!conversationData[sessionId]) {
        loadConversationData(sessionId);
      }
    }
    setExpandedSessions(newExpanded);
  };

  const loadConversationData = async (sessionId: string) => {
    try {
      const response = await statisticsApi.getConversationData(sessionId);
      if (response.success && response.data) {
        setConversationData(prev => {
          const newData = { ...prev };
          newData[sessionId] = response.data!; // Non-null assertion since we checked above
          return newData;
        });
      } else {
        toast.error('Failed to load conversation data');
      }
    } catch (error) {
      console.error('Error loading conversation data:', error);
      toast.error('Failed to load conversation data');
    }
  };

  const formatDate = (dateStr: string) => {
    // Handle both date-only format (YYYY-MM-DD) and date-time format (YYYY-MM-DD HH:00)
    if (dateStr.includes(' ')) {
      // Time format for 24-hour view: "2024-09-06 08:00" -> "8AM"
      const [, timePart] = dateStr.split(' ');
      const hour = parseInt(timePart.split(':')[0]);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${hour12}${ampm}`;
    } else {
      // Date-only format for longer periods: "2024-09-06" -> "Sep 6"
      const date = new Date(dateStr);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${monthName} ${day}`;
    }
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };


  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Show header only for new chatbot creation */}
      {!isEditing && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Create New Chatbot
          </h1>
        </div>
      )}

      {/* Tab Navigation and Content */}
      {isEditing ? (
        <div>
          {/* Integrated Card Tabs. The help affordance sits at the end of the row so
              there is exactly one per screen, in the same place on every tab (C5). */}
          <div className="flex items-center">
            {chatbotTabs.map((tab, index) => (
              <button
                key={tab.href}
                onClick={() => navigate(`/chatbots/${chatbotId}/${tab.href}`)}
                className={clsx(
                  'flex items-center space-x-2 px-5 py-3 text-sm transition-all duration-200 rounded-t-2xl border-t border-l border-r border-gray-200 dark:border-slate-700',
                  index > 0 && '-ml-px',
                  activeSection === tab.href
                    ? 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 font-medium'
                    : 'bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400'
                )}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
            <div className="ml-auto pr-2">
              <TabHelp section={activeSection} />
            </div>
          </div>

          {/* Content Area */}
          <div className="bg-white dark:bg-slate-800 rounded-b-2xl border border-gray-200 dark:border-slate-700 shadow-sm -mt-px">
            {/* Content */}
          {activeSection === 'settings' && (
          <form onSubmit={handleSubmit} className="px-8 pt-4 pb-8">
            {/* Simple/Advanced Toggle */}
            <div className="flex items-center justify-end mb-4">
              <div className="inline-flex items-center text-sm">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className={clsx(
                    'transition-colors',
                    !showAdvanced
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  )}
                >
                  Simple
                </button>
                <span className="mx-2 text-gray-300 dark:text-slate-600">|</span>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(true)}
                  className={clsx(
                    'transition-colors',
                    showAdvanced
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  )}
                >
                  Advanced
                </button>
              </div>
            </div>

            {/* Main Settings */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Chatbot Name - Half width */}
                <div className="lg:col-span-1">
                  <label htmlFor="name" className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Chatbot Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                      placeholder="My Awesome Chatbot"
                    />
                  </div>
                </div>

                {/* Description - Single line, same width as name */}
                <div className="lg:col-span-1">
                  <label htmlFor="description" className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Description
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="description"
                      id="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                      placeholder="Brief description of your chatbot"
                    />
                  </div>
                </div>

                {/* AI Model. Shown on Simple as well as Advanced — C2 had moved the
                    picker behind Advanced with a summary line here, which was reverted
                    on 2026-08-14: a two-line listbox carrying the cost reads as
                    information rather than as the intimidating control C2 was avoiding,
                    so hiding it stopped paying for itself. */}
                <ModelField
                  models={models}
                  modelsLoading={modelsLoading}
                  value={formData.modelId}
                  onSelect={id => setFormData(prev => ({ ...prev, modelId: id }))}
                  usage={usage}
                />
              </div>
            </div>

            {/* Advanced Settings - shown when toggle is on Advanced */}
            {showAdvanced && (
              <div className="pt-6 mt-6 border-t border-gray-200 dark:border-slate-700">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <label htmlFor="assistantIdentity" className="block text-sm text-gray-500 dark:text-gray-400">
                        Assistant Identity
                      </label>
                      <div className="relative ml-2 group">
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 w-72 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                          The first thing your chatbot is told about itself. Leave it empty and it introduces itself as the assistant for your website.
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        name="assistantIdentity"
                        id="assistantIdentity"
                        rows={2}
                        value={formData.assistantIdentity}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 resize-none"
                        placeholder="Default: the assistant for this website, answering visitors' questions about this business."
                      />
                      {formData.assistantIdentity && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, assistantIdentity: '' }))}
                          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                          title="Reset to the default identity"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <label htmlFor="customPrompt" className="block text-sm text-gray-500 dark:text-gray-400">
                        Custom Prompt
                      </label>
                      <div className="relative ml-2 group">
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Extra instructions added to your chatbot's prompt — tone, language, things to always or never say
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        name="customPrompt"
                        id="customPrompt"
                        rows={4}
                        value={formData.customPrompt}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 resize-none"
                        placeholder="Enter a custom system prompt to define your chatbot's behavior and personality..."
                      />
                      {formData.customPrompt && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, customPrompt: '' }))}
                          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                          title="Clear custom prompt"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Bot Unavailable Message */}
                  <div>
                      <div className="flex items-center mb-2">
                        <label htmlFor="botUnavailMsg" className="block text-sm text-gray-500 dark:text-gray-400">
                          Bot Unavailable Message
                        </label>
                        <div className="relative ml-2 group">
                          <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Custom message shown to visitors when bot is unavailable (paused, out of credits, etc.)
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          name="botUnavailMsg"
                          id="botUnavailMsg"
                          value={formData.botUnavailMsg}
                          onChange={handleInputChange}
                          className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                          placeholder="Message shown to visitors when this chatbot is unavailable"
                        />
                        {formData.botUnavailMsg && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, botUnavailMsg: '' }))}
                            className="absolute top-1/2 right-3 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                            title="Clear bot unavailable message"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                  </div>
                  </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/chatbots')}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  isEditing ? 'Update Chatbot' : 'Create Chatbot'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Knowledge Tab */}
        {activeSection === 'knowledge' && (
          <KnowledgePanel
            chatbotId={chatbotId}
            documents={documents}
            pendingDeletion={pendingDeletion}
            syncStatus={syncStatus}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            knowledgeMode={formData.knowledgeMode}
            onUploadFiles={handleFileUpload}
            onStageDelete={handleDeleteDocument}
            onUndoDelete={undoPendingDeletion}
            onDiscardChanges={handleDiscardChanges}
            onUpdateKnowledge={startSyncJob}
            onKnowledgeModeChange={handleKnowledgeModeChange}
          />
        )}

        {/* Test Tab */}
        {activeSection === 'test' && (
          <div className="px-8 pt-4 pb-8">
            {/* C4, third surface. Someone impatient opens Test while the content is
                still being read, gets a thin answer, and concludes the product does not
                work. Saying so at the TOP — above the conversation, not in a status bar
                on another tab — is what turns a bad first impression into a wait. */}
            {syncStatus?.status === 'IN_PROGRESS' && (
              <div className="mb-4 flex items-center gap-2 rounded-xl border border-primary-200 dark:border-primary-900/50 bg-primary-50 dark:bg-primary-900/20 px-4 py-2.5 text-sm text-primary-800 dark:text-primary-300">
                <span className="w-3.5 h-3.5 border-2 border-primary-300 border-t-primary-700 dark:border-primary-700 dark:border-t-primary-300 rounded-full animate-spin flex-shrink-0" />
                Still reading your sources — answers will be incomplete until this finishes.
              </div>
            )}
            {/* Chat Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => {
                  setMessages([{ id: '1', text: 'Hello! How can I help you today?', isUser: false, timestamp: new Date() }]);
                  setSessionId(undefined);
                  toast.success('Chat session reset');
                }}
                className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
              <div className="flex items-center gap-3">
                {/* Which answering behaviour this conversation is running under. */}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                  {formData.knowledgeMode === 'strict' ? 'Documents only' : 'Documents first'}
                </span>
                <span className="text-sm text-gray-400 dark:text-gray-500">Test your chatbot before deploying</span>
              </div>
            </div>

            {/* Chat Container */}
            <div className="h-[500px] flex flex-col">
            
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className="flex justify-start"
                  >
                    <div className="max-w-[90%] py-2">
                      <p className={clsx(
                        'text-xs mb-1',
                        message.isUser ? 'text-primary-700 dark:text-primary-400 font-semibold' : 'text-gray-400 dark:text-gray-500'
                      )}>
                        {message.isUser ? 'You' : 'Assistant'} • {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <div className={clsx(
                        'text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-headings:mt-3 prose-headings:mb-1 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:my-2 prose-pre:p-3 prose-pre:rounded-lg prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline dark:prose-invert',
                        message.isUser && '[--tw-prose-body:theme(colors.primary.900)] dark:[--tw-prose-invert-body:theme(colors.primary.300)]'
                      )}>
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%] py-2">
                      <p className="text-xs mb-1 text-gray-400 dark:text-gray-500">Assistant • typing...</p>
                      <div className="flex space-x-1 pt-1">
                        <div className="h-1.5 w-1.5 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="h-1.5 w-1.5 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="h-1.5 w-1.5 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce"></div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Invisible element to scroll to */}
                <div ref={messagesEndRef} />
              </div>
            
              {/* Message Input */}
              <div className="pt-4">
                <form onSubmit={handleSendMessage} className="flex space-x-2">
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors hover:border-gray-300 dark:hover:border-slate-500"
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || isSending}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-40"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Integrations Tab */}
        {activeSection === 'integration' && (
          <div className="px-8 pt-4 pb-8">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Website */}
                <div className="relative rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-5 hover:border-gray-300 dark:hover:border-slate-600 transition-colors lg:col-span-2">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3s-4.5 4.03-4.5 9 2.015 9 4.5 9z" />
                    </svg>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Website</h3>
                  </div>
                  <div className="mt-3">
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Add this chatbot to your website via WordPress plugin or embed code.
                    </p>

                    {/* Chatbot ID display */}
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-md">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Chatbot ID:
                      </label>
                      <div className="flex items-center space-x-2">
                        <code
                          className="flex-1 text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded border border-gray-200 dark:border-slate-500 font-mono text-gray-900 dark:text-gray-100 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700"
                          onClick={() => chatbotId && copyToClipboard(chatbotId)}
                          title="Click to copy"
                        >
                          {chatbotId || 'N/A'}
                        </code>
                        <button
                          onClick={() => chatbotId && copyToClipboard(chatbotId)}
                          className="text-xs text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
                          disabled={!chatbotId}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Use this ID for WordPress plugin setup or API integration.
                      </p>
                    </div>

                    <div className="mt-4">
                      <button
                        onClick={() => setShowDomainsModal(true)}
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-300 text-left">
                        {allowedEmbedDomains.length === 0
                          ? 'Manage Allowed Domains →'
                          : `Manage Allowed Domains (${allowedEmbedDomains.length}) →`}
                      </button>
                    </div>

                    {/* WordPress and Embed Code sub-cards */}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.405-.026-.78-.07-1.11m-7.981.105c.647-.03 1.232-.105 1.232-.105.582-.075.514-.93-.067-.899 0 0-1.755.135-2.88.135-1.064 0-2.85-.135-2.85-.135-.582-.031-.661.854-.079.899 0 0 .554.075 1.141.105l1.709 4.684-2.402 7.208-3.99-11.892c.647-.03 1.231-.105 1.231-.105.582-.075.516-.93-.066-.899 0 0-1.755.135-2.88.135-.202 0-.44-.005-.69-.015C2.566 4.753 6.056 2.5 10.21 2.5c3.065 0 5.855 1.166 7.94 3.077-.05-.005-.096-.008-.145-.008-1.064 0-1.818.93-1.818 1.932 0 .899.519 1.659 1.074 2.56.416.692.899 1.584.899 2.88 0 .964-.37 2.114-1.071 3.664L14.57 19.93l-3.405-10.098zm-6.611 1.031l3.129 8.563c.092.251.171.46.234.657-1.07-.267-2.04-.768-2.851-1.438 0 0-2.319-1.522-2.362-6.298-.034-3.79 1.85-1.484 1.85-1.484zm5.858 10.608c-.397.174-.818.317-1.252.417l3.325-9.612c.34-.81.73-1.673.73-2.48 0-.257-.016-.492-.046-.714 1.584 2.478 1.305 8.867-2.757 12.389z"/>
                          </svg>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">WordPress</h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          Install our WordPress plugin and enter the Chatbot ID above in the plugin settings.
                        </p>
                        <a
                          href="https://wordpress.org/plugins/celsian-chatbot"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          Get WordPress Plugin →
                        </a>
                      </div>

                      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                          </svg>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Embed Code</h4>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          Add a script tag to any HTML page to embed the chatbot widget.
                        </p>
                        <button
                          onClick={handleGetEmbedCode}
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-300 text-left">
                          Get Embed Instructions →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discord Bot */}
                <div className="relative rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-5 hover:border-gray-300 dark:hover:border-slate-600 transition-colors">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.195.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Discord Bot</h3>
                  </div>
                  <div className="mt-3">
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Deploy your chatbot as a Discord bot for your server.
                    </p>
                    {/* Not linked at all: show Link Discord button */}
                    {discordLinkStatus && !discordLinkStatus.linked && (
                      <div className="mt-2">
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-2">
                          Connect your Discord account to use this feature.
                        </p>
                        <button
                          onClick={handleLinkDiscord}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                        >
                          <svg className="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.195.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                          Link Discord Account
                        </button>
                      </div>
                    )}

                    {/* Linked: show Discord connection status */}
                    {discordLinkStatus && discordLinkStatus.linked && (
                      <div className="mt-2 flex items-center text-xs text-green-600 dark:text-green-400">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></div>
                        {discordLinkStatus.method === 'federated'
                          ? 'Connected via Discord login'
                          : `Discord linked: ${discordLinkStatus.discord_username}`}
                        {discordLinkStatus.method === 'linked' && (
                          <button
                            onClick={async () => {
                              try {
                                await discordApi.unlinkDiscordAccount();
                                toast.success('Discord account unlinked');
                                loadDiscordLinkStatus();
                              } catch (err) {
                                toast.error('Failed to unlink Discord account');
                              }
                            }}
                            className="ml-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            title="Unlink Discord account"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}

                    {/* Linked but tokens expired: show warning and refresh button */}
                    {discordLinkStatus && discordLinkStatus.linked && !discordLinkStatus.token_valid && (
                      <div className="mt-2">
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-2">
                          Your Discord connection has expired. Please refresh to manage servers.
                        </p>
                        <button
                          onClick={handleLinkDiscord}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                        >
                          <svg className="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.195.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                          Refresh Discord Connection
                        </button>
                      </div>
                    )}

                    {/* Show existing integrations (always, regardless of token state) */}
                    {discordIntegrations.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {discordIntegrations.map((integration) => (
                          <div key={integration.server_id} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                            <div className="flex items-center text-xs">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-green-800 dark:text-green-200 font-medium">
                                {integration.server_name}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDeleteDiscordIntegration(integration.server_id, integration.server_name)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs"
                              title="Disconnect"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Only show Setup/Connect button when Discord is linked AND tokens are valid */}
                    {discordLinkStatus && discordLinkStatus.linked && discordLinkStatus.token_valid && (
                      <button
                        onClick={() => setShowDiscordModal(true)}
                        className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {discordIntegrations.length > 0 ? 'Connect Another Server →' : 'Setup Discord Bot →'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
          </div>
        )}

        {/* Statistics Tab */}
        {activeSection === 'stats' && (
          <div className="px-8 pt-4 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              {/* Refresh Button (left) */}
              <button
                onClick={handleRefreshStatistics}
                disabled={statisticsLoading}
                className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                title="Refresh statistics"
              >
                <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                Refresh
              </button>

              {/* Time Range Toggle (right) */}
              <div className="inline-flex items-center text-sm">
                <button
                  onClick={() => handleStatisticsTimeRangeChange(1)}
                  disabled={statisticsLoading}
                  className={clsx(
                    'transition-colors disabled:opacity-50',
                    statisticsTimeRange === 1
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  )}
                >
                  24h
                </button>
                <span className="mx-2 text-gray-300 dark:text-slate-600">|</span>
                <button
                  onClick={() => handleStatisticsTimeRangeChange(7)}
                  disabled={statisticsLoading}
                  className={clsx(
                    'transition-colors disabled:opacity-50',
                    statisticsTimeRange === 7
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  )}
                >
                  7 days
                </button>
                <span className="mx-2 text-gray-300 dark:text-slate-600">|</span>
                <button
                  onClick={() => handleStatisticsTimeRangeChange(30)}
                  disabled={statisticsLoading}
                  className={clsx(
                    'transition-colors disabled:opacity-50',
                    statisticsTimeRange === 30
                      ? 'text-gray-900 dark:text-white font-medium'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  )}
                >
                  30 days
                </button>
              </div>
            </div>

            {statisticsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                {/* Charts Section */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
                  {/* Messages Over Time */}
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                    <h4 className="text-sm text-gray-500 dark:text-gray-400 mb-4">Messages Over Time</h4>
                    {dashboardData?.messages_over_time?.length === 0 ? (
                      <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                        <div className="text-center">
                          <ChartBarIcon className="mx-auto h-8 w-8 mb-2" />
                          <p className="text-sm">No messages yet</p>
                        </div>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={dashboardData?.messages_over_time || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={formatDate} />
                          <YAxis />
                          <Tooltip labelFormatter={(label) => `Date: ${formatDate(label)}`} />
                          <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* Active Sessions */}
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
                    <h4 className="text-sm text-gray-500 dark:text-gray-400 mb-4">Active Sessions</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={dashboardData?.active_sessions || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={formatDate} />
                        <YAxis />
                        <Tooltip labelFormatter={(label) => `Date: ${formatDate(label)}`} />
                        <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Sessions List */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm text-gray-500 dark:text-gray-400">Recent Sessions</h4>
                      <span className="text-xs text-gray-400 dark:text-gray-500">Click to view conversation</span>
                    </div>
                  </div>
                  
                  {sessionsData.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 dark:text-gray-400">No sessions found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-slate-700">
                      {sessionsData.slice(0, 10).map((session) => (
                        <div key={session.session_id}>
                          {/* Session Header */}
                          <div 
                            className="px-6 py-4 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer"
                            onClick={() => toggleSessionExpansion(session.session_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className={clsx(
                                  'h-2 w-2 rounded-full',
                                  session.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                                )}></div>
                                <div>
                                  <p className="text-sm text-gray-500 dark:text-gray-400" title={session.session_id}>
                                    {session.session_id.slice(0, 80)}...
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {session.total_messages} messages • {formatDuration(session.duration_seconds)} • {session.platform}
                                  </p>
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDateTime(session.created_at)}
                              </div>
                            </div>
                          </div>
                          
                          {/* Conversation Messages — the same flat transcript the
                              Test tab renders. The comment here used to claim
                              "Test Tab Style" while this view drew blue and grey
                              bubbles, so one conversation looked like two
                              different products depending on which tab you opened
                              it from. Now it genuinely matches: no bubbles, both
                              speakers left-aligned, named rather than colour-coded. */}
                          {expandedSessions.has(session.session_id) && (
                            <div className="px-6 py-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-600">
                              <div className="space-y-4 max-h-96 overflow-y-auto">
                                {conversationData[session.session_id] ? (
                                  conversationData[session.session_id].map((message, index) => (
                                    <div key={index} className="flex justify-start">
                                      <div className="max-w-[90%] py-2">
                                        <p className={clsx(
                                          'text-xs mb-1',
                                          message.message_type === 'user'
                                            ? 'text-primary-700 dark:text-primary-400 font-semibold'
                                            : 'text-gray-400 dark:text-gray-500'
                                        )}>
                                          {message.message_type === 'user' ? 'You' : 'Assistant'} •{' '}
                                          {new Date(parseInt(message.timestamp)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                        {/* Stored answers are markdown, and the Test tab
                                            renders them as such — as plain text the two
                                            views would still disagree, just differently. */}
                                        <div className={clsx(
                        'text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-headings:mt-3 prose-headings:mb-1 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:my-2 prose-pre:p-3 prose-pre:rounded-lg prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline dark:prose-invert',
                        message.message_type === 'user' && '[--tw-prose-body:theme(colors.primary.900)] dark:[--tw-prose-invert-body:theme(colors.primary.300)]'
                      )}>
                                          <ReactMarkdown>{message.content}</ReactMarkdown>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show All Sessions Button */}
                  {sessionsData.length > 10 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-600">
                      <button
                        onClick={() => setShowSessionsModal(true)}
                        className="w-full text-center py-2 px-4 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-md transition-colors duration-200"
                      >
                        Show All Sessions...
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 shadow-xl rounded-2xl border border-gray-200 dark:border-slate-700">
          <form onSubmit={handleSubmit} className="space-y-4 p-8">
            {/* Main Settings */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Chatbot Name - Half width */}
                <div className="lg:col-span-1">
                  <label htmlFor="name" className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Chatbot Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                      placeholder="My Awesome Chatbot"
                    />
                  </div>
                </div>

                {/* Description - Single line, same width as name */}
                <div className="lg:col-span-1">
                  <label htmlFor="description" className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Description
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="description"
                      id="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                      placeholder="Brief description of your chatbot"
                    />
                  </div>
                </div>

                {/* AI Model. Shown on Simple as well as Advanced — C2 had moved the
                    picker behind Advanced with a summary line here, which was reverted
                    on 2026-08-14: a two-line listbox carrying the cost reads as
                    information rather than as the intimidating control C2 was avoiding,
                    so hiding it stopped paying for itself. */}
                <ModelField
                  models={models}
                  modelsLoading={modelsLoading}
                  value={formData.modelId}
                  onSelect={id => setFormData(prev => ({ ...prev, modelId: id }))}
                  usage={usage}
                />
              </div>
            </div>

            {/* Advanced Settings - shown when toggle is on Advanced */}
            {showAdvanced && (
              <div className="pt-6 mt-6 border-t border-gray-200 dark:border-slate-700">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <label htmlFor="assistantIdentity" className="block text-sm text-gray-500 dark:text-gray-400">
                        Assistant Identity
                      </label>
                      <div className="relative ml-2 group">
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 w-72 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                          The first thing your chatbot is told about itself. Leave it empty and it introduces itself as the assistant for your website.
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        name="assistantIdentity"
                        id="assistantIdentity"
                        rows={2}
                        value={formData.assistantIdentity}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 resize-none"
                        placeholder="Default: the assistant for this website, answering visitors' questions about this business."
                      />
                      {formData.assistantIdentity && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, assistantIdentity: '' }))}
                          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                          title="Reset to the default identity"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <label htmlFor="customPrompt" className="block text-sm text-gray-500 dark:text-gray-400">
                        Custom Prompt
                      </label>
                      <div className="relative ml-2 group">
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Extra instructions added to your chatbot's prompt — tone, language, things to always or never say
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <textarea
                        name="customPrompt"
                        id="customPrompt"
                        rows={4}
                        value={formData.customPrompt}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 resize-none"
                        placeholder="Enter a custom system prompt to define your chatbot's behavior and personality..."
                      />
                      {formData.customPrompt && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, customPrompt: '' }))}
                          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                          title="Clear custom prompt"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Bot Unavailable Message */}
                  <div>
                      <div className="flex items-center mb-2">
                        <label htmlFor="botUnavailMsg" className="block text-sm text-gray-500 dark:text-gray-400">
                          Bot Unavailable Message
                        </label>
                        <div className="relative ml-2 group">
                          <svg className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          </svg>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-slate-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Custom message shown to visitors when bot is unavailable (paused, out of credits, etc.)
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          name="botUnavailMsg"
                          id="botUnavailMsg"
                          value={formData.botUnavailMsg}
                          onChange={handleInputChange}
                          className="block w-full px-4 py-2.5 pr-10 text-sm font-medium border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-0 focus:border-gray-400 dark:focus:border-slate-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500"
                          placeholder="Message shown to visitors when this chatbot is unavailable"
                        />
                        {formData.botUnavailMsg && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, botUnavailMsg: '' }))}
                            className="absolute top-1/2 right-3 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"
                            title="Clear bot unavailable message"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                  </div>
                  </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  'Create Chatbot'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Discord Server Selection Modal */}
      {isEditing && chatbotId && (
        <DiscordServerModal
          isOpen={showDiscordModal}
          onClose={() => setShowDiscordModal(false)}
          chatbotId={chatbotId}
          chatbotName={formData.name}
          onSuccess={handleDiscordIntegrationSuccess}
        />
      )}

      {/* Allowed Domains Modal */}
      {isEditing && chatbotId && (
        <AllowedDomainsModal
          isOpen={showDomainsModal}
          onClose={() => setShowDomainsModal(false)}
          domains={allowedEmbedDomains}
          onAddDomain={handleAddDomain}
          onRemoveDomain={handleRemoveDomain}
        />
      )}

      {/* File Replace Confirmation Dialog */}
      {fileConflict && (
        <FileReplaceConfirmDialog
          isOpen={true}
          onClose={handleFileReplaceCancel}
          onConfirm={handleFileReplaceConfirm}
          newFile={fileConflict.file}
          existingDocument={fileConflict.existingDocument}
        />
      )}

      {/* Sessions Modal */}
      {isEditing && chatbotId && (
        <SessionsModal
          isOpen={showSessionsModal}
          onClose={() => setShowSessionsModal(false)}
          chatbotId={chatbotId}
          initialStartDate={(() => {
            const end = new Date();
            const start = new Date(end.getTime() - (statisticsTimeRange * 24 * 60 * 60 * 1000));
            return start.toISOString();
          })()}
          initialEndDate={new Date().toISOString()}
        />
      )}

      {/* Website Embed Code Modal */}
      {showEmbedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Website Embed Code</h2>
                <button
                  onClick={() => setShowEmbedModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Embed Code Snippet */}
                <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Add this code to your website:
                  </label>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
{`<script>
window.celsianConfig = {
    chatbot_id: '${chatbotId}',
    api_url: 'https://converse.${window.location.hostname}/converse',
    platform: 'website'
    // widget_title: 'Chat Support',
    // greeting_message: 'Hi there! How can I help you today?',
    // position: 'bottom-right'  // bottom-left, top-right, top-left
    // hide_powered_by: true
};
</script>
<script src="${window.location.origin}/widget/v1/chatbot.js"></script>
<link rel="stylesheet" href="${window.location.origin}/widget/v1/themes/default.css">
<div id="celsian-chatbot-container"></div>
<script>CelsianChatbot.init();</script>`}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(`<script>
window.celsianConfig = {
    chatbot_id: '${chatbotId}',
    api_url: 'https://converse.${window.location.hostname}/converse',
    platform: 'website'
    // widget_title: 'Chat Support',
    // greeting_message: 'Hi there! How can I help you today?',
    // position: 'bottom-right'  // bottom-left, top-right, top-left
    // hide_powered_by: true
};
</script>
<script src="${window.location.origin}/widget/v1/chatbot.js"></script>
<link rel="stylesheet" href="${window.location.origin}/widget/v1/themes/default.css">
<div id="celsian-chatbot-container"></div>
<script>CelsianChatbot.init();</script>`)}
                    className="mt-2 px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                  >
                    Copy Full Code
                  </button>
                </div>

                {/* Instructions */}
                <div className="p-4 bg-gray-50 dark:bg-slate-800 border-l-4 border-primary-600 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Quick Start:</h3>
                  <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-1 list-decimal list-inside">
                    <li>Copy the code above</li>
                    <li>Paste it into your website's HTML before the closing &lt;/body&gt; tag</li>
                    <li>The chat widget will appear in the bottom-right corner</li>
                  </ol>
                </div>

                {/* Customization Examples */}
                <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Customization (Optional):</h3>
                  <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
{`/* Change widget colors */
.celsian-chat-icon {
    background: linear-gradient(135deg, #your-color 0%, #your-color2 100%) !important;
}

/* Reposition widget */
.celsian-position-bottom-right {
    bottom: 100px !important;
    right: 50px !important;
}`}
                  </pre>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowEmbedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-100 dark:bg-slate-900 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}